/**
 * Permission System Type Definitions
 *
 * This file defines the TypeScript types for the centralized permission system.
 * All pages use these types for consistent permission checks.
 *
 * The permission system supports two modes:
 * 1. Role-based: Users are assigned roles that bundle page permissions
 * 2. Advanced: Direct per-page permission configuration (legacy/fallback)
 */

import type { UserRoleAssignments } from './roles.types';

/**
 * Global user roles
 * - admin: Full access to all pages and all locations (bypasses all permission checks)
 * - user: Limited access based on pageAccess configuration
 */
export type GlobalRole = 'admin' | 'user';

/**
 * Location-specific roles
 * - editor: Can view and modify data for a location
 * - viewer: Can only view data for a location (read-only)
 */
export type LocationRole = 'editor' | 'viewer';

/**
 * Permission configuration for a single location
 */
interface LocationPermission {
  role: LocationRole;
}

/**
 * Page access configuration for location-based pages
 * (rentals, sales, vendors, labelPrinter)
 */
export interface LocationBasedPageAccess {
  enabled: boolean;
  locations?: Record<string, LocationPermission>; // Key is location ID (e.g., "11", "14")
}

/**
 * Page access configuration for simple boolean pages
 * (tracks, boltDecoder, equipmentSearch)
 */
export interface SimplePageAccess {
  enabled: boolean;
}

/**
 * Page access configuration for role-based pages (no locations)
 * User has a single role for the entire page
 * (fuelCalculator, directory, bobcatBook, suppliesBook, rentalReferences)
 */
export interface RoleBasedPageAccess {
  enabled: boolean;
  role?: LocationRole; // 'editor' or 'viewer'
}

/**
 * All page access configurations
 * Flexible structure allows adding new pages easily
 */
export interface PageAccessConfig {
  // Location-based pages
  rentals?: LocationBasedPageAccess;
  vendors?: LocationBasedPageAccess;
  backorders?: LocationBasedPageAccess;
  labelPrinter?: LocationBasedPageAccess;
  stamps?: LocationBasedPageAccess;
  truckingCompanies?: LocationBasedPageAccess;
  truckingCalendar?: LocationBasedPageAccess;
  yard?: LocationBasedPageAccess;
  archive?: LocationBasedPageAccess;
  billOfLading?: LocationBasedPageAccess;
  quizzes?: LocationBasedPageAccess;

  // Role-based pages (no locations)
  lostRevenue?: RoleBasedPageAccess;
  fuelCalculator?: RoleBasedPageAccess;
  directory?: RoleBasedPageAccess;
  bobcatBook?: RoleBasedPageAccess;
  suppliesBook?: RoleBasedPageAccess;
  rentalReferences?: RoleBasedPageAccess;

  // Simple pages
  tracks?: SimplePageAccess;
  boltDecoder?: SimplePageAccess;
  equipmentSearch?: SimplePageAccess; // Cross-location equipment search

  // Allow adding new pages without type errors
  [key: string]: LocationBasedPageAccess | SimplePageAccess | RoleBasedPageAccess | undefined;
}

/**
 * Complete user permissions document structure
 * Stored in Firestore at /users/{email}
 *
 * Supports two permission modes:
 * 1. Role-based (useAdvancedPermissions = false): Uses assignedRoles to determine access
 * 2. Advanced (useAdvancedPermissions = true): Uses pageAccess for granular control
 *
 * Existing users without useAdvancedPermissions field default to true (use pageAccess)
 * for backward compatibility.
 */
export interface UserPermissions {
  email: string;
  displayName?: string; // User's display name from Microsoft authentication
  role: GlobalRole;
  pageAccess: PageAccessConfig;
  defaultLocation?: string; // Default location ID (e.g., "11")

  /**
   * Role assignments - maps role IDs to their location assignments
   * Only used when useAdvancedPermissions is false
   *
   * @example
   * {
   *   'service-manager': { locations: ['11', '12'] },
   *   'parts-counter': { locations: ['11'] }
   * }
   */
  assignedRoles?: UserRoleAssignments;

  /**
   * Permission mode flag
   * - true: Use pageAccess for granular per-page control (legacy/advanced mode)
   * - false: Use assignedRoles for role-based permissions (recommended)
   * - undefined: Defaults to true for backward compatibility with existing users
   */
  useAdvancedPermissions?: boolean;

  /**
   * Division assignments - array of division IDs the user belongs to.
   * Used to filter which divisions (and their locations) the user can see.
   */
  divisions?: string[];

  /**
   * Claims version for Firebase Auth custom claims synchronization.
   * Incremented when permissions change. Frontend compares this with
   * the version in the ID token to know when to refresh.
   * This enables security rules to read permissions from JWT claims
   * instead of making Firestore get() calls (eliminating ~50-70% of reads).
   */
  claimsVersion?: number;
}

/**
 * Page configuration metadata
 * Used in page registry to define page characteristics
 */
/**
 * Admin-facing explanation of what each permission level does on a page,
 * shown in the Admin permission editors (PermissionHelpTip). Describe what
 * the app actually does, including cross-page dependencies.
 *
 * Location-based and role-based pages use `viewer` + `editor`; simple on/off
 * pages use `access` only (Viewer and Editor are identical there).
 * src/shared/config/__tests__/pagePermissionHelp.test.ts enforces this for
 * every grantable page.
 */
export interface PermissionHelp {
  /** Simple pages only: what having access lets the user do */
  access?: string;
  /** What a Viewer can do */
  viewer?: string;
  /** What an Editor can do in addition to Viewer */
  editor?: string;
  /** Caveats: requirements on other pages, admin-only extras, separate settings */
  notes?: string[];
}

export interface PageConfig {
  id: string; // Unique page identifier (matches pageAccess keys)
  displayName: string; // Human-readable page name
  path: string; // Route path (e.g., "/rentals")
  hasLocations: boolean; // Whether page uses location selector
  supportsRoles?: boolean; // Whether page uses editor/viewer roles (for non-location pages)
  isDivisionScoped?: boolean; // Whether page data is scoped to division (directory, references, lost revenue)
  requiresAuth: boolean; // Whether page requires authentication
  adminOnly?: boolean; // Whether page is restricted to admin users only
  description?: string; // Optional description for admin modal
  department?: 'shared' | 'parts' | 'rental' | 'supplies'; // Department grouping for admin UI
  isNew?: boolean; // Show "NEW" badge to highlight recently added pages
  permissionHelp?: PermissionHelp; // Required for every grantable page (see PermissionHelp)
  /**
   * A users/{email} flag (set only by admins, outside pageAccess and roles) whose holders
   * get Editor at every location here, as admins do. See holdsManagerFlag.
   */
  managerFlag?: ManagerFlag;
  /**
   * Location pages: only Editor grants open the page (availableLocations = Editor stores).
   * Viewer grants still exist and mean something elsewhere (Quizzes: the My Quizzes link).
   */
  editorsOnly?: boolean;
}

/** User-doc switches a page can name as its managerFlag. */
export type ManagerFlag = 'quizManager';

/** True when the user holds the page's manager flag (admins are handled separately). */
export function holdsManagerFlag(
  pageConfig: Pick<PageConfig, 'managerFlag'> | undefined,
  user: Partial<Record<ManagerFlag, unknown>> | null | undefined
): boolean {
  return !!pageConfig?.managerFlag && user?.[pageConfig.managerFlag] === true;
}

/**
 * Location ids a location-based grant opens on this page: every granted store, or only
 * the Editor ones when the page is editorsOnly.
 */
export function grantedLocationIds(
  pageConfig: Pick<PageConfig, 'editorsOnly'>,
  access: LocationBasedPageAccess
): string[] {
  const entries = Object.entries(access.locations || {});
  return (pageConfig.editorsOnly ? entries.filter(([, l]) => l?.role === 'editor') : entries).map(([id]) => id);
}

/**
 * Result returned by usePagePermissions hook
 */
export interface PagePermissionsResult {
  hasAccess: boolean; // Whether user can access this page
  isAdmin: boolean; // Whether user has admin role
  canEdit: boolean; // Whether user can edit (only for location-based pages)
  availableLocations: string[]; // List of location IDs user can access
  selectedLocation: string | null; // Currently selected location
  setSelectedLocation: (locationId: string) => void; // Function to change location
  isLoading: boolean; // Whether permissions are still loading
  userPermissions: UserPermissions | null; // Full user permissions object
  locationWasSwitched: boolean; // True when auto-switch occurred (selected location wasn't available)
  switchedFromLocation: string | null; // The location user was on before auto-switch (if any)
  selectedDivision: string | null; // Currently selected division ID
  availableDivisions: string[]; // Division IDs available to this user
}





/**
 * Type guard to check if page access is location-based
 */
export function isLocationBasedAccess(
  access: LocationBasedPageAccess | SimplePageAccess | undefined
): access is LocationBasedPageAccess {
  return access !== undefined && 'locations' in access;
}


/**
 * Type guard to check if page access is role-based
 */
export function isRoleBasedAccess(
  access: LocationBasedPageAccess | SimplePageAccess | RoleBasedPageAccess | undefined
): access is RoleBasedPageAccess {
  return access !== undefined && 'role' in access && !('locations' in access);
}
